import { Component, OnDestroy, OnInit, computed, inject, signal } from '@angular/core'
import { FormsModule } from '@angular/forms'
import { Router } from '@angular/router'
import { SidebarComponent } from '../layout/sidebar'
import { DetallePanelComponent } from '../components/detalle-panel'
import { ExportModalComponent } from '../components/export-modal'
import { FotoRemotaComponent } from '../components/foto-remota'
import { CrearRegistroModalComponent } from '../components/crear-registro-modal'
import { listarProductos, listarProveedores, suscribirCambiosProductos } from '../../application/casos-de-uso'
import { estaListo, etiquetaProveedor, type Producto, type Proveedor } from '../../domain/entidades'
import { AuthService } from '../../infrastructure/auth/auth.service'
import { puedeCompletarFichaDinamico } from '../../infrastructure/permisos/cliente'

/** Mostramos el tablero de consolidacion con filtros, agrupacion y actualizacion en tiempo real. */
const CLAVE_SIDEBAR_COLAPSADO = 'feria-china-2026:sidebar-colapsado'
const CLAVE_TEMA_CLARO = 'feria-china-2026:tema-claro'

// Mismas letras que `PREFIJOS` en presentation/pages/movil/inicio.ts — el codigo de
// proveedor ya las trae embebidas (`PROV-<letra>-NNN`), asi que filtrar aca es solo
// comparar ese prefijo, sin tocar el modelo de datos ni Supabase.
export const CAPTURADORES = ['A', 'J', 'W', 'T', 'C']

function leerColapsadoInicial(): boolean {
  try {
    const guardado = localStorage.getItem(CLAVE_SIDEBAR_COLAPSADO)
    if (guardado !== null) return guardado === '1'
    // Sin preferencia guardada: en pantalla angosta (celular) arranca colapsado —
    // el sidebar fijo de 252px no cabe comodo en un viewport de ~375px.
    return typeof window !== 'undefined' && window.innerWidth < 768
  } catch {
    return false
  }
}

function leerTemaClaroInicial(): boolean {
  try {
    const guardado = localStorage.getItem(CLAVE_TEMA_CLARO)
    // Sin preferencia guardada todavia: arranca en claro (pedido explicito).
    // Si alguien ya eligio explicitamente oscuro ('0'), se respeta esa eleccion.
    return guardado === null ? true : guardado === '1'
  } catch {
    return true
  }
}

@Component({
  selector: 'app-dashboard',
  standalone: true,
  imports: [FormsModule, SidebarComponent, DetallePanelComponent, ExportModalComponent, FotoRemotaComponent, CrearRegistroModalComponent],
  templateUrl: './dashboard.html',
  styleUrl: './dashboard.scss',
})
export class DashboardComponent implements OnDestroy, OnInit {
  private readonly router = inject(Router)
  private readonly auth = inject(AuthService)

  readonly productos = signal<Producto[]>([])
  readonly proveedores = signal<Proveedor[]>([])
  readonly activoId = signal<string | null>(null)
  readonly mostrarExport = signal(false)
  /** Crear proveedor/producto desde el dashboard (pedido de Angie, 2026-09-16) — mismo gate de rol que ya usa completar ficha. */
  readonly mostrarCrearRegistro = signal(false)

  get puedeCrearRegistro(): boolean {
    const perfil = this.auth.perfil()
    return perfil ? puedeCompletarFichaDinamico(perfil.rol) : false
  }
  readonly busqueda = signal('')
  /** null = todos los capturadores. El boton de exportar ignora este filtro a proposito. */
  readonly filtroCapturador = signal<string | null>(null)

  readonly capturadores = CAPTURADORES

  readonly sidebarColapsado = signal(leerColapsadoInicial())
  /**
   * Tema claro/oscuro del dashboard. Por defecto queda en claro (pedido
   * explicito) — el login sigue siendo siempre oscuro, esto es solo el
   * dashboard. Persistido igual que el colapso del sidebar: una vez que
   * alguien elige oscuro a proposito, esa eleccion se respeta. Ver
   * Instruccion_Tema_Claro/Dashboard_TemaClaro_Instruccion.md.
   */
  readonly temaClaro = signal(leerTemaClaroInicial())

  /** Boton "Capturar" visible solo en movil (ver dashboard.html) — simetrico al
   *  "Ver tablero" de movil/inicio.html, sin depender de abrir el sidebar-drawer. */
  async irACapturar(): Promise<void> {
    await this.router.navigateByUrl('/movil')
  }

  alternarSidebar(): void {
    this.sidebarColapsado.update((v) => !v)
    try {
      localStorage.setItem(CLAVE_SIDEBAR_COLAPSADO, this.sidebarColapsado() ? '1' : '0')
    } catch {
      // Si localStorage falla, el colapso sigue funcionando, solo no se recuerda.
    }
  }

  /** Cierra el sidebar-drawer de movil desde la "X" o el fondo oscuro — a diferencia
   *  de `alternarSidebar()` no alterna, siempre cierra (tocar afuera no debe abrir). */
  cerrarSidebarMovil(): void {
    this.sidebarColapsado.set(true)
    try {
      localStorage.setItem(CLAVE_SIDEBAR_COLAPSADO, '1')
    } catch {
      // Si localStorage falla, el cierre sigue funcionando, solo no se recuerda.
    }
  }

  alternarTema(): void {
    this.temaClaro.update((v) => !v)
    try {
      localStorage.setItem(CLAVE_TEMA_CLARO, this.temaClaro() ? '1' : '0')
    } catch {
      // Si localStorage falla, el tema sigue funcionando, solo no se recuerda.
    }
  }

  private readonly desuscribir = suscribirCambiosProductos(() => void this.recargar())

  // Refrescamos al recuperar conexion o foco por si el canal realtime se interrumpio.
  private readonly alVolverSenal = () => void this.recargar()
  private readonly alVolverVisible = () => {
    if (document.visibilityState === 'visible') void this.recargar()
  }

  ngOnInit(): void {
    void this.recargar()
    window.addEventListener('online', this.alVolverSenal)
    document.addEventListener('visibilitychange', this.alVolverVisible)
  }

  ngOnDestroy(): void {
    this.desuscribir()
    window.removeEventListener('online', this.alVolverSenal)
    document.removeEventListener('visibilitychange', this.alVolverVisible)
  }

  async recargar(): Promise<void> {
    const [p, prov] = await Promise.all([listarProductos(), listarProveedores()])
    this.productos.set(p)
    this.proveedores.set(prov)
    if (this.activoId() === null) this.activoId.set(p[0]?.id ?? null)
  }

  nombreProveedor(codigo: string): string {
    return this.proveedores().find((p) => p.codigo === codigo)?.nombre || codigo
  }

  /**
   * "PROV-A-008 · Lucky" para las etiquetas de grupo — a diferencia de
   * nombreProveedor() (que hace fallback al código, útil para búsqueda), acá
   * evitamos mostrar "PROV-A-008 · PROV-A-008" cuando no hay nombre.
   */
  etiquetaGrupo(codigo: string): string {
    return etiquetaProveedor(codigo, this.proveedores().find((p) => p.codigo === codigo)?.nombre)
  }

  fotoTarjetaProveedor(codigo: string): string | undefined {
    return this.proveedores().find((p) => p.codigo === codigo)?.fotoTarjetaUrl
  }

  readonly filtrados = computed(() => {
    const q = this.busqueda().trim().toLowerCase()
    const letra = this.filtroCapturador()
    let productos = this.productos()
    if (letra) productos = productos.filter((p) => p.proveedorCodigo.startsWith(`PROV-${letra}-`))
    if (!q) return productos
    return productos.filter(
      (p) => p.mueble.toLowerCase().includes(q) || this.nombreProveedor(p.proveedorCodigo).toLowerCase().includes(q),
    )
  })

  cambiarFiltroCapturador(letra: string): void {
    this.filtroCapturador.set(this.filtroCapturador() === letra ? null : letra)
  }

  readonly agrupados = computed(() => {
    const grupos = new Map<string, Producto[]>()
    for (const p of this.filtrados()) {
      const lista = grupos.get(p.proveedorCodigo) ?? []
      lista.push(p)
      grupos.set(p.proveedorCodigo, lista)
    }
    return [...grupos.entries()]
  })

  readonly totales = computed(() => {
    const productos = this.productos()
    return {
      proveedores: new Set(productos.map((p) => p.proveedorCodigo)).size,
      productos: productos.length,
      telas: productos.reduce((acc, p) => acc + p.telas.length, 0),
      sinPrecio: productos.filter((p) => !estaListo(p)).length,
    }
  })

  readonly activo = computed(() => this.productos().find((p) => p.id === this.activoId()) ?? null)

  estaListo(p: Producto): boolean {
    return estaListo(p)
  }

  ultimoSegmentoId(id: string): string {
    return id.split('-').at(-1) ?? ''
  }

  /**
   * Foto real para la miniatura del listado: la del mueble si existe, si no
   * la de frente de la primera tela. `colorFrente`/`colorReverso` (el
   * fallback de `fondoTela`) no los escribe ningun flujo real de captura
   * movil — solo aparecen en datos de demo — asi que con datos reales casi
   * siempre hay foto antes que color, y mostrarla es mas honesto que un
   * degradado "pendiente" cuando en realidad ya hay algo capturado.
   */
  fotoDeProducto(p: Producto): string | null {
    return p.fotos?.[0]?.url || p.telas[0]?.fotoFrenteUrl || null
  }

  /**
   * Gradiente de respaldo, solo cuando no hay ninguna foto todavia. Si el
   * producto no tiene tela asociada (Angie captura el mueble primero, la
   * tela es un paso aparte), usamos los colores de marca en vez de un gris
   * plano — se lee como "pendiente de tela", no como un placeholder roto.
   */
  fondoTela(p: Producto): string {
    const t = p.telas[0]
    const inicio = t?.colorFrente || 'rgba(239, 29, 45, 0.3)'
    const fin = t?.colorReverso || 'rgba(18, 28, 43, 0.9)'
    return `linear-gradient(135deg, ${inicio}, ${fin})`
  }
}
